import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import API from '@api/API';

import { CurrentEntertainer, useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useSweepstakes } from '@queries/SweepstakesQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { FeaturedPhotoManagementSection } from '@screens/entertainer/components/FeaturedPhotoManagementSection';

const usePublishSweepstakesCollabPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: async ({ entertainerMediaId }: { entertainerMediaId: string }) => {
      return API.post<{ message?: string }, { entertainerMediaId: string }>(
        `/v1/user/challenges/publish-sweepstakes-post`,
        {
          entertainerMediaId,
        },
      );
    },
  });

  return {
    isPending,
    publishCollabPost: async (entertainerMediaId: string) => {
      return mutateAsync({ entertainerMediaId });
    },
  };
};

export const ShareFeaturedLookCollabPostScreen = createScreenComponent<{ cityId?: string }>(
  ({ cityId, back }) => {
    const { currentCity } = useCurrentUserCity();
    const [cityIdOverride, setCityIdOverride] = useState(cityId);
    const [didAuth, setDidAuth] = useState(false);
    const { entertainer } = useCurrentEntertainerQuery();
    const { sweepstakes } = useSweepstakes();
    const { publishCollabPost } = usePublishSweepstakesCollabPostMutation();
    const { track } = useAnalytics();
    const { navigate, back: navigateBack } = useRouter();
    const { createAuthGateHandler } = useAuthGate();

    const collabPostTask = sweepstakes?.tasks.find(({ reason }) => {
      return reason.includes('Instagram');
    });

    const handleShareLook = async (media: CurrentEntertainer['featuredMedia'][number]) => {
      track('Share Featured Look Collab Post', {
        mediaId: media._id,
      });
      try {
        const response = await publishCollabPost(media._id);
        if (!response?.message) {
          navigateBack();
        }
      } catch (error) {
        Sentry.captureException(error, {
          data: { mediaId: media._id },
        });
      }
    };

    useEffect(() => {
      if (didAuth) {
        navigate('OpenPopup::AddLook', {
          state: { cityId: cityIdOverride ?? cityId ?? currentCity?._id },
        });
      }
    }, [didAuth]);

    const handleLogIn = () => {
      createAuthGateHandler(
        () => {
          setDidAuth(true);
        },
        {
          cityId: cityIdOverride ?? cityId ?? currentCity?._id,
          entertainerCallback: (payload) => {
            setCityIdOverride(payload.cityId);
          },
        },
      );
    };

    return (
      <Screen
        name="Share Featured Look Collab Post Screen"
        hideToolbar
        disableLocationPermissionGate
        headerProps={{
          back: { to: back ?? '/challenges' },
          title: <Screen.GagglTitle />,
        }}
      >
        <Screen.Content maxWidth={425}>
          <div className="d-flex mb-3 gap-3 align-items-center">
            <div style={{ fontSize: 48, lineHeight: 1 }}>
              <FontAwesomeIcon name="instagram" faStyle="fab" />
            </div>
            <div>
              <strong className="fs-5">Share & Earn</strong>
              <br />
              <span>{collabPostTask?.title}</span>
            </div>
          </div>

          {entertainer?._id ? (
            <>
              {collabPostTask?.description && mapStringToParagraphs(collabPostTask.description)}
              <FeaturedPhotoManagementSection
                hideTitleToolbar
                cityId={cityIdOverride ?? cityId ?? currentCity?._id}
                featuredMedia={entertainer?.featuredMedia ?? []}
                shareLookOverride={handleShareLook}
                shareLookOverrideButtonText="Share Sweepstakes Post"
              />
            </>
          ) : (
            <>
              <EmptyState
                title="Log in to continue"
                text={collabPostTask.description}
                button={{
                  text: 'Earn 10,000 Entries',
                  onClick: handleLogIn,
                }}
              />
            </>
          )}
        </Screen.Content>
      </Screen>
    );
  },
);
